import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Check } from 'lucide-react';
import { packages, themes } from '../data/mockData';
import { useLanguage } from '../translations/LanguageContext';

const Home = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-[#2e5453]">
      {/* Hero Section */}
      <section className="relative bg-[#2e5453] overflow-hidden">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-3xl md:text-5xl font-bold leading-tight text-[#96ba1e]">
                {t('home.heroTitle')}
              </h1>
              <p className="text-lg text-[#c4d88f] leading-relaxed whitespace-pre-line">
                {t('home.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453] px-8 py-6 text-lg font-bold">
                    {t('nav.bookNow')}
                  </Button>
                </Link>
                <Link to="/packages">
                  <Button size="lg" variant="outline" className="border-[#96ba1e] text-[#96ba1e] hover:bg-[#3d6a68] px-8 py-6 text-lg">
                    {t('home.explorePackages')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1756621716915-cb8723574795?w=800"
                  alt="Happy children at birthday party"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#3d6a68]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#96ba1e]">{t('home.featuresTitle')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="bg-[#2e5453] border-[#96ba1e] border">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">🏠</div>
                <h3 className="text-lg font-bold text-[#96ba1e] mb-2">{t('home.featureFormat')}</h3>
                <p className="text-[#c4d88f] text-sm">{t('home.featureFormatDesc')}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#2e5453] border-[#96ba1e] border">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">🍰</div>
                <h3 className="text-lg font-bold text-[#96ba1e] mb-2">{t('home.featureFood')}</h3>
                <p className="text-[#c4d88f] text-sm">{t('home.featureFoodDesc')}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#2e5453] border-[#96ba1e] border">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">🥤</div>
                <h3 className="text-lg font-bold text-[#96ba1e] mb-2">{t('home.featureDrinks')}</h3>
                <p className="text-[#c4d88f] text-sm">{t('home.featureDrinksDesc')}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#2e5453] border-[#96ba1e] border">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">🚫🍷</div>
                <h3 className="text-lg font-bold text-[#96ba1e] mb-2">{t('home.featureAtmosphere')}</h3>
                <p className="text-[#c4d88f] text-sm">{t('home.featureAtmosphereDesc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-16 bg-[#2e5453]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] mb-4">
              {t('home.unforgettableBirthdays')}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-[#96ba1e]">
              {t('home.pickYourMagic')}
            </h2>
            <p className="text-[#c4d88f] text-lg max-w-2xl mx-auto">
              {t('home.pickDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative transition-all hover:shadow-xl bg-[#3d6a68] border-[#96ba1e] ${
                  pkg.popular ? 'border-2 shadow-lg scale-105' : 'border hover:scale-105'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] px-4 py-1">
                      {t('home.mostPopular')}
                    </Badge>
                  </div>
                )}
                {pkg.bestValue && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-green-500 text-white hover:bg-green-600 px-3 py-1">
                      {t('home.bestValue')}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-[#96ba1e]">{pkg.name}</h3>
                    <div className="mt-2">
                      <span className="text-4xl font-bold text-[#96ba1e]">€{pkg.price}</span>
                      <span className="text-[#c4d88f] ml-2">{t('home.perParty')}</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="w-5 h-5 text-[#96ba1e] flex-shrink-0 mt-0.5" />
                        <span className="text-[#c4d88f]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/booking" state={{ selectedPackage: pkg.id }}>
                    <Button
                      className={`w-full ${
                        pkg.popular
                          ? 'bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453]'
                          : 'bg-[#2e5453] hover:bg-[#3d6a68] text-[#96ba1e] border-2 border-[#96ba1e]'
                      }`}
                    >
                      Select {pkg.name}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/packages">
              <Button size="lg" className="bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453]">
                {t('home.viewAllPackages')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Themes Section - Only 2 cards */}
      <section className="py-16 bg-[#3d6a68]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#96ba1e]">{t('home.popularThemes')}</h2>
            <p className="text-[#c4d88f] text-lg max-w-2xl mx-auto">
              {t('home.themesDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {themes.map((theme) => (
              <Card key={theme.id} className="group overflow-hidden hover:shadow-xl transition-all bg-[#2e5453] border-[#96ba1e] border">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={theme.image}
                    alt={theme.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <Badge className="absolute top-3 right-3 bg-[#96ba1e] text-[#2e5453]">
                    {theme.tag}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl mb-2 text-[#96ba1e]">{theme.name}</h3>
                  <p className="text-[#c4d88f] mb-4">{theme.description}</p>
                  <Link to="/booking">
                    <Button variant="link" className="text-[#96ba1e] p-0 h-auto font-semibold">
                      {t('home.viewDetails')} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2e5453] border-t border-[#3d6a68]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#96ba1e]">
            {t('home.readyToCreate')}
          </h2>
          <p className="text-xl mb-8 text-[#c4d88f] max-w-2xl mx-auto">
            {t('home.readyDescription')}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] px-10 py-6 text-lg font-bold">
              {t('home.bookYourParty')} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
